<template>
  <sui-grid class="summary" :columns="3">
    <sui-grid-row>
      <!-- Classifications -->
      <sui-grid-column id="classification-eln">
        <h4 class="summary-subheader">ELN 2017 Risk Score</h4>
        <div class="summary-content">
          <div v-if="elnLoading">
            <img src="../assets/loader.gif" />
          </div>
          <div v-else-if="elnCategory" :class="['ui label upper', elnCategory]">
            {{ elnCategory }}
          </div>
          <div v-else class="ui label upper undefined">UNDEFINED</div>
        </div>
      </sui-grid-column>

      <sui-grid-column id="classification-new">
        <h4 class="summary-subheader">Tazi et al Proposed Risk Score</h4>
        <div class="summary-content">
          <div v-if="newLoading">
            <img src="../assets/loader.gif" />
          </div>
          <div v-else-if="newCategory" :class="['ui label upper', newCategory]">
            {{ newCategory }}
          </div>
          <div v-else class="ui label upper undefined">UNDEFINED</div>
        </div>
      </sui-grid-column>

      <sui-grid-column id="classification-component">
        <h4 class="summary-subheader">Molecular Classification</h4>
        <div class="summary-content">
          <div v-if="newLoading">
            <img src="../assets/loader.gif" />
          </div>
          <div
            v-else-if="newPrincipleComponent"
            :class="['ui label', newPrincipleComponent]"
          >
            {{ componentLabel }}
          </div>
          <div v-else class="ui label upper undefined">UNDEFINED</div>
        </div>
      </sui-grid-column>
    </sui-grid-row>

    <sui-grid-row>
      <!-- Clinical Fields -->
      <sui-grid-column
        v-for="(fieldValue, fieldName) in patientData"
        :key="fieldName"
        v-show="fieldName in fieldDefinitions"
      >
        <div v-if="fieldName in fieldDefinitions" class="summary-field">
          {{ fieldDefinitions[fieldName].label }}:
        </div>
        <div
          v-if="fieldName in fieldDefinitions"
          class="summary-value"
          v-html="getValue(fieldName, fieldValue)"
        ></div>
      </sui-grid-column>

      <!-- Mutations -->
      <sui-grid-column
        v-for="(mutationTypeList, mutationType) in patientMutations"
        :key="mutationType"
        v-show="mutationTypeList.length"
      >
        <div class="summary-field">{{ mutationType }} Mutations:</div>
        <div class="summary-value">
          {{ displayMutationList(mutationTypeList) }}
        </div>
      </sui-grid-column>
    </sui-grid-row>

    <sui-grid-row>
      <span v-if="!isTheModelConfident" class="cohort ci-error">
        * These values are outside the 95% percentile band of the cohort. Beware
        the patient model will have lower confidence because of this.
      </span>
    </sui-grid-row>

    <!-- Cohort Plots -->
    <sui-grid-row id="cohort-row" class="cohort-row">
      <span class="cohort-title">Cohort Distribution (n=3201)</span>
      <cohort-plots />
    </sui-grid-row>
  </sui-grid>
</template>

<script>
import CohortPlots from "@/components/CohortPlots";
import fieldDefinitions from "@/utils/fields";
import { mutationList } from "@/utils/mutations";
import { mapGetters } from "vuex";

export default {
  name: "PatientSummary",
  data() {
    return {
      fieldDefinitions
    };
  },
  components: {
    CohortPlots
  },
  computed: {
    ...mapGetters([
      "patientData",
      "patientMutations",
      "thisPatient",
      "newClassification",
      "elnClassification",
      "newCategory",
      "elnCategory",
      "newPrincipleComponent",
      "thisPatientLoading",
      "elnLoading",
      "newLoading",
      "hasResults",
      "confidenceIntervals"
    ]),
    filteredPatientMutations() {
      return this.patientMutations;
    },
    componentLabel() {
      let value = this.newPrincipleComponent.replace(/_/g, " ");
      if (["no events"].includes(value)) {
        value = value.toUpperCase();
      }
      return value;
    },
    isTheModelConfident() {
      return Object.keys(this.patientData).every(fieldName => {
        return this.validateCI(fieldName, this.patientData[fieldName]);
      });
    }
  },
  methods: {
    displayMutationList(mutationTypeList) {
      const mutationTexts = mutationTypeList.map(mutation => {
        const match = mutationList.filter(m => m.key === mutation);
        return match.length ? match[0].text : "";
      });
      return mutationTexts.join(", ");
    },
    getValue(fieldName, fieldValue) {
      let value =
        "values" in fieldDefinitions[fieldName]
          ? fieldDefinitions[fieldName].values[fieldValue]
          : fieldValue;
      let units = fieldDefinitions[fieldName].units || "";

      const isConfident = this.validateCI(fieldName, fieldValue);

      return isConfident
        ? `${value} ${units}`
        : `<span class="ci-error">${value} ${units} *</span>`;
    },
    validateCI(fieldName, fieldValue) {
      if (fieldName in this.confidenceIntervals) {
        const [lowCI, highCI] = this.confidenceIntervals[fieldName];
        if (fieldValue < lowCI) {
          return false;
        } else if (fieldValue > highCI) {
          return false;
        }
      }
      return true;
    }
  }
};
</script>

<style lang="scss">
.ui.grid.summary {
  font-size: 14px;
  padding: 14px;
  font-family: DM Sans;
  td,
  div {
    color: #00113d;
    text-align: center;
    &.summary-value {
      display: inline-block;
      font-weight: bold;
      min-width: 120px;
      margin-right: 8px;
      line-height: 22px;
    }
    &.summary-field {
      min-width: 190px;
      margin-right: 8px;
      line-height: 18px;
    }
  }
  .ci-error {
    color: #ff2525;
    text-align: center;
    width: 100%;
    &.cohort {
      border-top: 1px solid #edeffa;
      margin: -1px;
      padding: 10px;
    }
  }
  h4.summary-subheader,
  div.summary-content {
    color: #00113d;
    font-weight: bold;
    min-width: 120px;
    text-align: center;
    margin-bottom: 4px;
    .ui.label {
      color: white;
      &.undefined {
        background-color: #ccc;
      }
    }
  }
  .row {
    padding: 0;
    .column {
      margin-top: 0;
      margin-bottom: 0;
      border-top: 0px solid #edeffa;
      border-bottom: 1px solid #edeffa;
      padding-top: 14px;
      padding-bottom: 14px;
      line-height: 28px;
    }
    &:last-child {
      border-top: 1px solid #edeffa;
      margin: -1px;
    }
  }
  .upper {
    text-transform: uppercase;
  }
  span.cohort-title {
    font-weight: bold;
    min-width: 120px;
    margin-right: 8px;
    padding: 14px;
  }
  .cohort-row {
    justify-content: center;
  }
}
</style>
